import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, X, User, LogOut, ChevronDown, Search } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { CONTACT_INFO, categories } from '../../data/mock';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { cartCount, openCart } = useCart();
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setShowCategories(false);
    setShowUserMenu(false);
  }, [location]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Shop All', path: '/shop' },
    { name: 'About', path: '/about' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <header className="sticky top-0 left-0 right-0 z-50">
      {/* Promo Code Bar */}
      <div className="bg-gradient-to-r from-orange-600 via-orange-500 to-orange-600 text-white text-xs sm:text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-10 flex items-center justify-center gap-2 sm:gap-3 text-center">
          <span className="hidden sm:inline-block text-base">🎉</span>
          <p className="tracking-wide font-medium">
            <span className="hidden sm:inline">Limited Time Offer: </span>
            <span className="font-bold">20% OFF</span> your order — Use code{' '}
            <span className="inline-flex items-center px-2 py-0.5 bg-white text-orange-700 font-extrabold tracking-widest rounded-md mx-1">SAVE20</span>
            <span className="hidden md:inline"> at checkout</span>
          </p>
          <span className="hidden sm:inline-block text-base">🎉</span>
        </div>
      </div>

      {/* Top Announcement Bar */}
      <div className="bg-orange-700 text-white text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between">
          <p className="hidden sm:block tracking-wide">
            <span className="font-semibold">Free USA Shipping</span> &middot; 2-4 Day Delivery
          </p>
          <p className="tracking-wide">
            Need help? Call <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="font-semibold hover:underline">{CONTACT_INFO.phone}</a>
          </p>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <div className="flex flex-col leading-tight">
                <span className="text-xl sm:text-2xl font-extrabold text-gray-900 tracking-tight">
                  EC <span className="text-orange-600">Pedal</span> Works
                </span>
                <span className="hidden sm:block text-[10px] tracking-[0.25em] uppercase text-gray-500 font-medium">
                  Ride in Style
                </span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                      isActive
                        ? 'text-orange-600 bg-orange-50'
                        : 'text-gray-700 hover:text-orange-600 hover:bg-gray-50'
                    }`}
                  >
                    {link.name}
                  </Link>
                );
              })}

              {/* Categories Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setShowCategories(true)}
                onMouseLeave={() => setShowCategories(false)}
              >
                <button className="px-4 py-2 rounded-md text-sm font-semibold text-gray-700 hover:text-orange-600 hover:bg-gray-50 flex items-center gap-1">
                  Categories
                  <ChevronDown className="w-4 h-4" />
                </button>
                {showCategories && (
                  <div className="absolute right-0 top-full pt-2 w-64">
                    <div className="bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden py-2">
                      {categories.map((cat) => (
                        <Link
                          key={cat.id}
                          to={`/shop?category=${cat.slug}`}
                          className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors"
                        >
                          {cat.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-1 sm:gap-2">
              {/* Account */}
              <div
                className="relative hidden sm:block"
                onMouseEnter={() => setShowUserMenu(true)}
                onMouseLeave={() => setShowUserMenu(false)}
              >
                <button
                  onClick={() => isAuthenticated ? null : navigate('/login')}
                  className="w-10 h-10 rounded-full flex items-center justify-center text-gray-700 hover:text-orange-600 hover:bg-gray-100 transition-colors"
                  aria-label="Account"
                >
                  <User className="w-5 h-5" />
                </button>
                {showUserMenu && (
                  <div className="absolute right-0 top-full pt-2 w-56">
                    <div className="bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden py-2">
                      {isAuthenticated ? (
                        <>
                          <div className="px-4 py-2 border-b border-gray-100">
                            <p className="text-xs text-gray-500">Signed in as</p>
                            <p className="text-sm font-semibold text-gray-900 truncate">{user?.email}</p>
                          </div>
                          <button
                            onClick={handleLogout}
                            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            <LogOut className="w-4 h-4" />
                            Sign Out
                          </button>
                        </>
                      ) : (
                        <>
                          <Link to="/login" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600">Sign In</Link>
                          <Link to="/register" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600">Create Account</Link>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Cart */}
              <button
                onClick={openCart}
                className="relative w-10 h-10 rounded-full flex items-center justify-center text-gray-700 hover:text-orange-600 hover:bg-gray-100 transition-colors"
                aria-label="Cart"
              >
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </button>

              {/* Mobile menu toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden w-10 h-10 rounded-full flex items-center justify-center text-gray-700 hover:bg-gray-100"
                aria-label="Menu"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white">
            <div className="max-w-7xl mx-auto px-4 py-3 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="block px-4 py-3 text-base font-semibold text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg"
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-3 border-t border-gray-200 mt-3">
                <p className="px-4 text-xs uppercase tracking-wider text-gray-500 mb-2">Categories</p>
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/shop?category=${cat.slug}`}
                    className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
              <div className="pt-3 border-t border-gray-200 mt-3">
                {isAuthenticated ? (
                  <button onClick={handleLogout} className="block w-full text-left px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 rounded-lg">
                    Sign Out
                  </button>
                ) : (
                  <>
                    <Link to="/login" className="block px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg">Sign In</Link>
                    <Link to="/register" className="block px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg">Create Account</Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
